import { useAscendLocale } from '../session/useAscendLocale';
import type { Locale } from './presentation-types';

// ---------------------------------------------------------------------------
// Catalogue de traduction fortement typé
// fr-FR = langue source et fallback ; chaque clé absente en EN/ES retombe
// silencieusement sur fr-FR via la fonction de résolution t().
// ---------------------------------------------------------------------------

export type TranslationCatalog = Record<string, string>;

const fr: TranslationCatalog = {
  // ── Navigation ──────────────────────────────────────────────────────
  'nav.today': "Aujourd'hui",
  'nav.missions': 'Missions',
  'nav.progression': 'Progression',
  'nav.ressources': 'Ressources',
  'nav.aria': 'Navigation principale',

  // ── Header ──────────────────────────────────────────────────────────
  'header.brand': 'ASCEND INVICTUS',

  // ── Hero ────────────────────────────────────────────────────────────
  'hero.greeting.morning': 'Bonjour',
  'hero.greeting.afternoon': 'Bon après-midi',
  'hero.greeting.evening': 'Bonsoir',

  // ── Lignes directrices ──────────────────────────────────────────────
  'guidelines.label': 'LIGNES DIRECTRICES DU JOUR',
  'guidelines.routines': '3 ROUTINES INDISPENSABLES',

  // ── Mode ────────────────────────────────────────────────────────────
  'mode.selector.label': 'Mode de la journée',
  'mode.selector.footer': 'Le mode pilote la logique fonctionnelle — jamais l\'impact émotionnel.',
  'mode.confirm': 'Confirmer le mode',
  'mode.pending': 'Enregistrement…',
  'mode.cancel': 'Annuler',
  'mode.changer': 'Changer de mode',
  'mode.label': 'MODE',
  // 9 modes
  'mode.normal': 'Normal',
  'mode.elevation': 'Élévation',
  'mode.charge': 'Charge',
  'mode.difficile': 'Difficile',
  'mode.voyage': 'Voyage',
  'mode.transition': 'Transition',
  'mode.moment_decisif': 'Moment décisif',
  'mode.imprevu': 'Imprévu',
  'mode.recuperation': 'Récupération',
  // 9 intentions de mode
  'mode.normal.intention': 'Clarté. Discipline. Maîtrise.',
  'mode.charge.intention': 'Réduire au nécessaire, sans négliger l\'essentiel.',
  'mode.voyage.intention': 'Rester connecté à l\'essentiel, où que tu sois.',
  'mode.recuperation.intention': 'Reconstruire sans culpabilité.',
  'mode.difficile.intention': 'Traverser. Juste traverser.',
  'mode.elevation.intention': 'Approfondir, pas accumuler.',
  'mode.moment_decisif.intention': 'Tout ce qui n\'est pas la décision peut attendre.',
  'mode.imprevu.intention': 'L\'essentiel tient en une phrase.',
  'mode.transition.intention': 'La continuité avant la maîtrise.',
  // 9 conséquences de mode (modeCopy.ts)
  'mode.consequence.normal': 'La routine complète reste active, sans restriction.',
  'mode.consequence.busy': "Seules les missions obligatoires et la mission facultative essentielle compteront dans la progression. Le sport devient une séance courte et le bilan un format raccourci. Rien n'est supprimé : le reste de la routine reste visible.",
  'mode.consequence.travel': "Les horaires restent souples et le sport devient un mouvement sans équipement. La routine complète reste active.",
  'mode.consequence.recovery': "Le sport devient une récupération active, sans haute intensité. La routine complète reste active.",
  'mode.consequence.difficult': "Seules l'eau, la respiration, dix minutes de mouvement, une mission essentielle et un bilan court compteront dans la progression. Aucune mission existante n'est supprimée.",
  'mode.consequence.elevation': "La routine complète reste active, à l'exigence maximale : s'élever consiste à approfondir le geste, pas à multiplier les gestes.",
  'mode.consequence.moment_decisif': "Seules les missions obligatoires et la mission facultative essentielle compteront dans la progression. Tout ce qui n'est pas la décision peut attendre.",
  'mode.consequence.imprevu': "Seules les missions obligatoires et la mission facultative essentielle compteront dans la progression. La journée a changé de forme ; l'essentiel est gardé.",
  'mode.consequence.transition': "La routine complète reste active, à exigence basse. Entre deux régimes, la continuité prime sur la maîtrise immédiate.",
  // 9 pensées de mode (Hero mantra)
  'mode.normal.thought': '« La maîtrise commence par le choix de ce qui mérite ton énergie. »',
  'mode.charge.thought': '« Ce qui n\'est pas essentiel n\'a pas à entrer dans la journée. »',
  'mode.voyage.thought': '« Les repères changent ; la direction demeure. »',
  'mode.recuperation.thought': '« La récupération fait partie de la progression. »',
  'mode.difficile.thought': '« Une journée difficile n\'exige pas une conduite parfaite, mais une conduite juste. »',
  'mode.elevation.thought': '« S\'élever consiste à approfondir le geste, pas à multiplier les gestes. »',
  'mode.moment_decisif.thought': '« Préparer ce qui dépend de soi, puis entrer pleinement dans le moment. »',
  'mode.imprevu.thought': '« Accepter le changement de plan sans abandonner sa direction. »',
  'mode.transition.thought': '« Ne pas exiger immédiatement la maîtrise d\'une vie qui vient de changer. »',
  // 9 modes × 3 routines
  'mode.normal.routine.0': 'Commencer avec intention',
  'mode.normal.routine.1': "Réaliser l'action essentielle",
  'mode.normal.routine.2': 'Terminer avec clarté',
  'mode.charge.routine.0': 'Réduire au strict nécessaire',
  'mode.charge.routine.1': 'Protéger le bloc essentiel',
  'mode.charge.routine.2': 'Refuser les distractions inutiles',
  'mode.voyage.routine.0': "Garder un point d'ancrage",
  'mode.voyage.routine.1': 'Adapter sans abandonner',
  'mode.voyage.routine.2': "Intégrer l'expérience du déplacement",
  'mode.recuperation.routine.0': "Réduire l'intensité",
  'mode.recuperation.routine.1': 'Restaurer le corps',
  'mode.recuperation.routine.2': "Préparer le retour à l'équilibre",
  'mode.difficile.routine.0': "Garder uniquement l'essentiel",
  'mode.difficile.routine.1': "Avancer une étape après l'autre",
  'mode.difficile.routine.2': "Préserver sa maîtrise jusqu'au soir",
  'mode.elevation.routine.0': 'Choisir le domaine à élever',
  'mode.elevation.routine.1': 'Exécuter le geste exigeant',
  'mode.elevation.routine.2': "Mesurer le progrès avec humilité",
  'mode.moment_decisif.routine.0': "Clarifier l'intention",
  'mode.moment_decisif.routine.1': 'Préparer le geste décisif',
  'mode.moment_decisif.routine.2': 'Éliminer toute distraction inutile',
  'mode.imprevu.routine.0': "Sécuriser l'essentiel",
  'mode.imprevu.routine.1': 'Recomposer la journée',
  'mode.imprevu.routine.2': 'Abandonner le non-indispensable',
  'mode.transition.routine.0': 'Accepter le nouveau rythme',
  'mode.transition.routine.1': 'Installer un premier repère',
  'mode.transition.routine.2': 'Poser une action fondatrice',

  // ── État émotionnel ─────────────────────────────────────────────────
  'state.selector.label': 'État émotionnel',
  'state.clear': 'Aucun état déclaré',
  'state.clear.short': 'AUCUN ÉTAT',
  'state.confirm': "Confirmer l'état",
  'state.pending': 'Enregistrement…',
  'state.cancel': 'Annuler',
  'state.declarer': 'Déclarer un état',
  'state.label': 'ÉTAT',
  // 8 familles
  'family.rayonnement': 'RAYONNEMENT',
  'family.serenite': 'SÉRÉNITÉ',
  'family.pression_peur': 'PRESSION / PEUR',
  'family.friction': 'FRICTION',
  'family.vulnerabilite': 'VULNÉRABILITÉ',
  'family.reparation': 'RÉPARATION',
  'family.recentrage': 'RECENTRAGE',
  'family.coup_dur': 'COUP DUR',
  // Intentions
  'family.rayonnement.intention': "Accueillir l'élan sans perdre l'axe.",
  'family.serenite.intention': 'Tenir ce qui est juste, sans le forcer.',
  'family.pression_peur.intention': 'Réduire le champ à ce qui dépend de toi.',
  'family.friction.intention': 'Laisser retomber avant de trancher.',
  'family.vulnerabilite.intention': 'Traverser sans se juger.',
  'family.reparation.intention': 'Réparer sans forcer.',
  'family.recentrage.intention': "Retrouver l'axe avant de reprendre la marche.",
  'family.coup_dur.intention': 'Tenir, simplement.',
  // Pensées
  'family.rayonnement.thought': 'Ce qui va bien mérite la même attention que ce qui résiste.',
  'family.serenite.thought': "Le calme n'est pas l'absence de mouvement, c'est la justesse du rythme.",
  'family.pression_peur.thought': 'La peur rétrécit le champ. Une seule chose à la fois le rouvre.',
  'family.friction.thought': 'La colère renseigne sur ce qui compte. Elle ne décide pas.',
  'family.vulnerabilite.thought': 'Ce que tu vaux ne se mesure pas à la journée que tu viens de vivre.',
  'family.reparation.thought': 'Se reposer est aussi un acte de discipline.',
  'family.recentrage.thought': "Perdre la direction n'est pas perdre le chemin.",
  'family.coup_dur.thought': "Tenir aujourd'hui suffit. Le reste attendra demain.",
  // Routines
  'family.rayonnement.routine.0': 'Commencer avec intention',
  'family.rayonnement.routine.1': "Réaliser l'action essentielle",
  'family.rayonnement.routine.2': 'Terminer avec clarté',
  'family.serenite.routine.0': 'Commencer sans hâte',
  'family.serenite.routine.1': "Réaliser l'action essentielle",
  'family.serenite.routine.2': 'Clore la journée en silence',
  'family.pression_peur.routine.0': 'Nommer ce qui inquiète',
  'family.pression_peur.routine.1': 'Faire la seule action qui dépend de toi',
  'family.pression_peur.routine.2': 'Poser la journée avant la nuit',
  'family.friction.routine.0': 'Différer la réaction',
  'family.friction.routine.1': "Réaliser l'action essentielle",
  'family.friction.routine.2': 'Reprendre le sujet à froid',
  'family.vulnerabilite.routine.0': 'Commencer petit',
  'family.vulnerabilite.routine.1': 'Faire une seule chose utile',
  'family.vulnerabilite.routine.2': 'Terminer sans bilan',
  'family.reparation.routine.0': 'Se lever sans injonction',
  'family.reparation.routine.1': 'Bouger doucement',
  'family.reparation.routine.2': 'Se coucher plus tôt',
  'family.recentrage.routine.0': 'Écrire ce qui a changé',
  'family.recentrage.routine.1': "Faire l'action la plus simple",
  'family.recentrage.routine.2': 'Ne rien décider ce soir',
  'family.coup_dur.routine.0': 'Boire, manger, dormir',
  'family.coup_dur.routine.1': 'Faire une seule chose',
  'family.coup_dur.routine.2': 'Prévenir une personne',
  // 36 états (domaine → catalogue)
  'state.joie': 'Joie',
  'state.accomplissement': 'Accomplissement',
  'state.serenite': 'Sérénité',
  'state.sous_pression': 'Sous pression',
  'state.stress': 'Stress',
  'state.anxiete': 'Anxiété',
  'state.inquietude': 'Inquiétude',
  'state.incertitude': 'Incertitude',
  'state.apprehension': 'Appréhension',
  'state.colere': 'Colère',
  'state.frustration': 'Frustration',
  'state.irritation': 'Irritation',
  'state.impatience': 'Impatience',
  'state.injustice': 'Injustice',
  'state.conflit_mineur': 'Conflit mineur',
  'state.solitude': 'Solitude',
  'state.rejet': 'Rejet',
  'state.honte': 'Honte',
  'state.culpabilite': 'Culpabilité',
  'state.doute_profond': 'Doute profond',
  'state.insuffisance': "Sentiment d'insuffisance",
  'state.malade': 'Malade',
  'state.epuise': 'Épuisé',
  'state.convalescence': 'Convalescence',
  'state.baisse_physique': 'Baisse physique importante',
  'state.echec': 'Échec',
  'state.deception': 'Déception',
  'state.perte_de_reperes': 'Perte de repères',
  'state.confusion': 'Confusion',
  'state.transition_interieure': 'Transition intérieure',
  'state.doute_direction': 'Doute sur la direction',
  'state.rupture': 'Rupture ou conflit affectif majeur',
  'state.deuil': 'Deuil ou perte',
  'state.moral_bas': 'Moral très bas',
  'state.mauvaise_nouvelle': 'Mauvaise nouvelle profonde',
  'state.choc_personnel': 'Choc personnel important',

  // ── Missions ────────────────────────────────────────────────────────
  'missions.label': 'Missions du jour',
  'missions.reorganiser': 'Réorganiser',
  'missions.enregistrer_ordre': "Enregistrer l'ordre",
  'missions.ordre_enregistre': 'Ordre enregistré pour aujourd\'hui.',
  'missions.ordre_enregistre_futur': 'Ordre enregistré pour aujourd\'hui et les prochaines journées.',
  'missions.reorg_annulee': 'Réorganisation annulée.',
  'missions.reorg_active': 'Mode réorganisation activé.',
  'missions.deplacee': 'Mission déplacée en position',
  'missions.postpone_title': 'Reporter la mission',
  'missions.postpone_confirm': 'Confirmer le report',
  'missions.skip_title': 'Ignorer cette mission ?',
  'missions.skip_confirm': 'Ignorer aujourd\'hui',
  'missions.skip_body': 'La mission restera visible dans l\'historique, sans être reprogrammée. Aucun jugement : tu pourras poursuivre l\'essentiel.',
  'missions.reorder_scope_today': "Aujourd'hui uniquement",
  'missions.reorder_scope_future': "Aujourd'hui et prochaines journées",
  'missions.reorder_scope_legend': 'Portée du nouvel ordre',
  'missions.postpone_legend': 'Choisir une date future',
  'missions.postpone_tomorrow': 'Demain',
  'missions.postpone_2days': 'Dans 2 jours',
  'missions.postpone_custom': 'Choisir une date',
  'missions.postpone_custom_aria': 'Date de report personnalisée',
  'missions.empty_title': 'Journée libre',
  'missions.empty_message': 'Aucune mission active n\'est prévue aujourd\'hui.',
  'missions.out_of_mode': 'Hors du mode actuel : cette mission reste disponible mais ne compte pas dans la progression du jour.',

  // ── Actions de mission ──────────────────────────────────────────────
  'action.start': 'Démarrer',
  'action.complete': 'Terminer',
  'action.complete_direct': 'Terminer directement',
  'action.reset': 'Remettre à faire',
  'action.reactivate': 'Réactiver',
  'action.skip': 'Ignorer',
  'action.postpone': 'Reporter',
  'action.monter': 'Monter',
  'action.descendre': 'Descendre',
  'action.annuler': 'Annuler',
  'action.enregistrer': 'Enregistrer',
  'action.reorganiser': 'Réorganiser',
  'action.ouvrir': 'Ouvrir',
  'action.fermer': 'Fermer',
  'action.valider': 'Valider',

  // ── Catégories de mission ───────────────────────────────────────────
  'category.mental': 'Mental',
  'category.physique': 'Physique',
  'category.strategique': 'Stratégique',
  'category.relationnel': 'Relationnel',
  'category.logistique': 'Logistique',
  'category.administratif': 'Administratif',
  'category.creation': 'Création',
  'category.apprentissage': 'Apprentissage',
  'category.sport': 'Sport',
  'category.reflexion': 'Réflexion',
  'category.rituel': 'Rituel',
  'category.bien_etre': 'Bien-être',
  'category.finance': 'Finance',
  'category.communication': 'Communication',
  // RoutineCategory-specific labels
  'category.health': 'Santé',
  'category.work': 'Travail',
  'category.organization': 'Organisation',
  'category.growth': 'Croissance',
  'category.emotion': 'Émotion',
  'category.relationship': 'Relation',
  'category.journal': 'Journal',
  'category.custom': 'Personnel',
  // Mission detail panel labels
  'mission.objective_label': 'Objectif',
  'mission.instructions_label': 'À faire',
  'mission.aria_label': 'la mission {title}',
  'mission.order_aria': 'Mission {n}',
  'mission.actions_aria': 'Actions pour {title}',
  'mission.out_of_mode': 'Hors mode',
  'mission.provisional': 'Contenu provisoire',
  'mission.mode_note': 'Hors du mode actuel : cette mission reste disponible mais ne compte pas dans la progression du jour.',

  // ── Priorité ────────────────────────────────────────────────────────
  'priority.essentiel': 'ESSENTIEL',
  'priority.recommande': 'RECOMMANDÉ',

  // ── Statuts de mission ──────────────────────────────────────────────
  'status.done': 'FAIT',
  'status.deferred': 'DIFFÉRÉ',
  'status.postponed_label': 'Reportée au',
  'status.postponed': 'Reportée',
  'status.completed': 'Terminée',
  'status.pending': 'À faire',
  'status.in_progress': 'En cours',
  'status.skipped': 'Ignorée',

  // ── Badge Hors mode ─────────────────────────────────────────────────
  'badge.out_of_mode': 'Hors mode',

  // ── Feedback ────────────────────────────────────────────────────────
  'feedback.mission_started': 'Mission démarrée.',
  'feedback.mission_completed': 'Mission terminée.',
  'feedback.mission_reset': 'Mission remise à faire.',
  'feedback.mission_skipped': 'Mission ignorée pour aujourd\'hui.',
  'feedback.mission_postponed': 'Mission reportée au',
  'feedback.adaptation_applied': 'Adaptation appliquée pour aujourd\'hui.',
  'feedback.adaptation_kept': 'Ordre actuel conservé.',
  'feedback.reorg_cancelled': 'Réorganisation annulée.',

  // ── Progression ─────────────────────────────────────────────────────
  'progress.label': 'Progression quotidienne',
  'progress.aria': 'Progression {percent}%',
  'progress.message.idle': 'Une seule mission suffit pour lancer la journée.',
  'progress.message.launched': 'Tu as lancé le mouvement.',
  'progress.message.continuing': "Continue sur l'essentiel.",
  'progress.message.advancing': 'La journée avance.',
  'progress.message.solid': 'Journée solide.',
  'progress.message.ascended': 'Ascension accomplie.',

  // ── Sport ───────────────────────────────────────────────────────────
  'sport.label': 'Sport',
  'sport.empty': 'Aucune séance prévue aujourd\'hui.',
  'sport.loading': 'Chargement de la séance…',
  'sport.safety': 'Arrête l\'exercice en cas de douleur anormale, vertige, malaise ou essoufflement inhabituel.',
  'sport.status.pending': 'À faire',
  'sport.status.in_progress': 'En cours',
  'sport.status.completed': 'Terminée',
  'sport.status.reduced': 'Réduite',
  'sport.exercise.complete': 'Terminer',
  'sport.exercise.replace': 'Remplacer',
  'sport.exercise.skip': 'Ignorer',
  'sport.exercise.status.pending': 'À faire',
  'sport.exercise.status.completed': 'Terminé',
  'sport.exercise.status.skipped': 'Ignoré',
  'sport.exercise.status.replaced': 'Remplacé',
  'sport.weight_label': 'Poids choisi (facultatif)',
  'sport.reduce': 'Réduire la séance',
  'sport.units.reps': 'répétitions',
  'sport.units.minutes': 'minutes',
  'sport.units.seconds': 'secondes',
  'sport.reduce_aria': 'Réduire la séance de sport',
  'sport.weight_aria': 'Poids choisi pour {name}',
  'sport.complete_aria': 'Terminer {name}',
  'sport.replace_aria': 'Remplacer {name}',
  'sport.skip_aria': 'Ignorer {name}',
  // 12 exercices du catalogue
  'exercise.treadmill-walk.name': 'Marche rapide sur tapis',
  'exercise.treadmill-walk.instructions': 'Marche à un rythme soutenu, sans forcer l\'allure.',
  'exercise.stationary-bike.name': 'Vélo stationnaire',
  'exercise.stationary-bike.instructions': 'Pédale à une cadence régulière, résistance modérée.',
  'exercise.bench-press.name': 'Développé couché',
  'exercise.bench-press.instructions': 'Choisis un poids que tu peux contrôler sur toute l\'amplitude.',
  'exercise.dumbbell-row.name': 'Rowing haltère',
  'exercise.dumbbell-row.instructions': 'Dos plat, tire l\'haltère vers la hanche sans à-coup.',
  'exercise.goblet-squat.name': 'Squat gobelet',
  'exercise.goblet-squat.instructions': 'Descends jusqu\'à ce que les cuisses soient parallèles au sol si possible.',
  'exercise.dumbbell-shoulder-press.name': 'Développé épaules',
  'exercise.dumbbell-shoulder-press.instructions': 'Pousse les haltères au-dessus de la tête sans cambrer le dos.',
  'exercise.push-ups.name': 'Pompes',
  'exercise.push-ups.instructions': 'Genoux au sol si nécessaire, amplitude complète.',
  'exercise.bodyweight-squats.name': 'Squats au poids du corps',
  'exercise.bodyweight-squats.instructions': 'Descends à ton amplitude confortable, rythme régulier.',
  'exercise.jumping-jacks.name': 'Jumping jacks',
  'exercise.jumping-jacks.instructions': 'Rythme modéré, respiration régulière.',
  'exercise.plank.name': 'Gainage',
  'exercise.plank.instructions': 'Corps aligné, respiration continue.',
  'exercise.guided-stretch.name': 'Étirements guidés',
  'exercise.guided-stretch.instructions': 'Étire chaque groupe musculaire sans à-coup ni douleur.',
  'exercise.breathing-walk.name': 'Marche respirée',
  'exercise.breathing-walk.instructions': 'Marche à un rythme confortable en respirant lentement.',

  // ── Inspiration stoïcienne ──────────────────────────────────────────
  'inspiration.label': 'Inspiration stoïcienne',
  'inspiration.empty': 'Aucune pensée disponible aujourd\'hui.',
  'inspiration.loading': 'Chargement…',

  // ── Guidance adaptative ─────────────────────────────────────────────
  'guidance.label': 'Guidance adaptative',
  'guidance.empty': 'Aucune suggestion aujourd\'hui.',
  'guidance.accept': 'Accepter',
  'guidance.accept.aria': 'Accepter la suggestion d\'adaptation',
  'guidance.reject': "Garder l'ordre actuel",
  'guidance.reject.aria': "Garder l'ordre actuel",
  'guidance.accepted': '✓ Adaptation appliquée',
  'guidance.rejected': 'Ordre actuel conservé',
  // Guidance message codes (AdaptationCycle express())
  'guidance.summary.abstain_default': 'Aucune adaptation proposée aujourd\'hui.',
  'guidance.summary.abstain_ags_disabled': 'Aucune adaptation proposée aujourd\'hui.',
  'guidance.summary.abstain_observation_failed': 'Aucune adaptation proposée aujourd\'hui.',
  'guidance.summary.respect_accepted': 'Adaptation déjà acceptée pour aujourd\'hui.',
  'guidance.summary.respect_rejected': 'Adaptation déjà refusée pour aujourd\'hui.',
  'guidance.summary.propose': 'Réorganiser les missions ?',
  'guidance.reason.abstain_default': 'L\'ordre actuel convient déjà.',
  'guidance.reason.abstain_ags_disabled': 'Les suggestions d\'adaptation sont désactivées.',
  'guidance.reason.abstain_observation_failed': 'Le contexte du jour n\'a pas pu être établi.',
  'guidance.reason.respect_accepted': 'Adaptation déjà acceptée.',
  'guidance.reason.respect_rejected': 'Adaptation déjà refusée.',
  'guidance.reason.propose': 'Une réorganisation est proposée.',
  'guidance.reason.with_rule': 'Mode {mode} : {rule}.',
  'guidance.rule.required_first': 'les missions obligatoires en premier',
  'guidance.rule.active_for_mode_first': 'les missions adaptées au voyage en premier',
  'guidance.rule.essential_first': 'la mission essentielle en premier',
  'guidance.rule.essential_then_required': 'la mission essentielle puis les obligatoires en premier',

  // ── Historique ──────────────────────────────────────────────────────
  'history.title': 'Historique',
  'history.empty': 'Aucune journée enregistrée.',
  'history.loading': 'Ouverture de l’historique…',
  'history.error': 'Impossible de charger l\'historique.',
  'history.eyebrow': '{count} journées récentes',
  'history.description': 'Regarde le chemin parcouru, sans jugement.',
  'history.empty_title': 'Le chemin commence ici',
  'history.empty_message': 'Ta première journée apparaîtra dès que tu ouvriras Aujourd’hui.',
  'history.list': 'Liste',
  'history.calendar': 'Calendrier',
  'history.completed': 'Terminée',
  'history.open': 'Ouverte',
  'history.view_aria': 'Vue de l’historique',
  'history.section_aria': 'Journées récentes',
  'history.progress_aria': 'Progression du {date}',
  'history.detail_loading': 'Ouverture de la journée…',
  'history.back': 'Retour à l’historique',
  'history.readonly': 'Lecture seule',
  'history.day_completed': 'Journée terminée',
  'history.day_open': 'Journée ouverte',
  'history.detail_progress_aria': 'Progression de cette journée',
  'history.progress_label': 'Progression historique',
  'history.missions_completed': '{completed} sur {active} missions terminées',
  'history.readonly_missions_aria': 'Missions en lecture seule',

  // ── Réglages ────────────────────────────────────────────────────────
  'settings.title': 'Réglages',
  'settings.profile': 'Profil',
  'settings.profile_description': 'Ces informations restent sur ton appareil.',
  'settings.first_name': 'Prénom',
  'settings.first_name_placeholder': 'Ton prénom',
  'settings.language': 'Langue',
  'settings.language_description': 'Choisis la langue de l\'interface. Le changement est immédiat et persistant.',
  'settings.language.fr': 'Français',
  'settings.language.en': 'English',
  'settings.language.es': 'Español',

  // ── Données & confidentialité ───────────────────────────────────────
  'data.title': 'Données & confidentialité',
  'data.ownership_eyebrow': 'Tes données t’appartiennent',
  'data.description': 'Exporte, restaure ou efface l’intégralité de tes données locales.',
  'data.export': 'Exporter mes données',
  'data.exporting': 'Export en cours…',
  'data.export_description': 'Télécharge une copie complète de tes missions, journées, journaux et séances de sport au format JSON. Ce fichier n’est pas chiffré : conserve-le en lieu sûr.',
  'data.import': 'Importer une sauvegarde',
  'data.import_description': 'Remplace intégralement tes données actuelles par le contenu d’un fichier exporté précédemment. Une sauvegarde de sécurité de l’état actuel est effectuée avant l’import.',
  'data.choose_file': 'Choisir un fichier',
  'data.choose_file_aria': 'Choisir un fichier de sauvegarde à importer',
  'data.delete': 'Supprimer toutes mes données',
  'data.delete_description': 'Efface définitivement toutes tes missions, journées, journaux, séances et réglages locaux. Cette action est irréversible.',
  'data.delete_title': 'Supprimer toutes les données ?',
  'data.delete_final': 'Supprimer définitivement',
  'data.import_confirm': 'Confirmer l’import',
  'data.import_replace': 'Importer et remplacer',
  'data.import_preview_intro': 'Ce fichier contient :',
  'data.import_preview_days': '{count} journées',
  'data.import_preview_missions': '{count} missions',
  'data.import_preview_journal': '{count} entrées de journal',
  'data.import_preview_workouts': '{count} séances de sport',
  'data.import_warning': 'Toutes tes données actuelles seront remplacées. Cette action est irréversible.',
  'data.export_success': 'Données exportées avec succès.',
  'data.import_success': 'Import terminé. Tes données ont été remplacées par le contenu du fichier. Une sauvegarde de ton état précédent vient d’être téléchargée.',
  'data.delete_confirm': 'Confirmer la suppression',
  'data.delete_warning': 'Toutes tes missions, journées, journaux, séances et réglages locaux seront supprimés définitivement. Cette action est irréversible.',
  'data.delete_success': 'Toutes tes données ont été supprimées. Une nouvelle journée t’attend.',
  'data.import_preview': 'Aperçu avant import',

  // ── États d'interface (panels génériques) ───────────────────────────
  'states.loading': 'Chargement de la journée…',
  'states.storage_unavailable': 'Stockage local indisponible',
  'states.prep_interrupted': 'Préparation locale interrompue',
  'states.prep_failed': 'Impossible de préparer la journée',
  'states.diagnostic': 'Diagnostic technique',
  'states.empty_eyebrow': 'Espace disponible',

  // ── Calendrier d'activité ───────────────────────────────────────────
  'calendar.aria': 'Calendrier d’activité',
  'calendar.day_aria': '{date} · {percent}% de progression',
  'calendar.less': 'Moins',
  'calendar.more': 'Plus',
  'calendar.sun': 'D',
  'calendar.mon': 'L',
  'calendar.tue': 'M',
  'calendar.wed': 'M',
  'calendar.thu': 'J',
  'calendar.fri': 'V',
  'calendar.sat': 'S',

  // ── Repli / dépli ───────────────────────────────────────────────────
  'collapsible.expand': 'Déplier',
  'collapsible.collapse': 'Replier',

  // ── Erreurs ─────────────────────────────────────────────────────────
  'error.loading': 'La journée ne peut pas être chargée.',
  'error.retry': 'Réessayer',
  'error.generic': 'Une erreur est survenue.',
  'error.not_found': 'Page introuvable.',

  // ── Architecture (écran accessible) ─────────────────────────────────
  'architecture.title': 'Architecture',

  // ── Version ─────────────────────────────────────────────────────────
  'version.label': 'Version de l’application',
  'version.field': 'Version',
  'version.commit': 'Commit',
  'version.build_date': 'Date de build',

  // ── Missions (seed MVP) ─────────────────────────────────────────────
  'mission.wake_without_phone.title': 'Réveil sans téléphone',
  'mission.wake_without_phone.objective': 'Commencer la journée sans distraction.',
  'mission.wake_without_phone.instruction.0': 'Ne consulte pas les notifications.',
  'mission.wake_without_phone.instruction.1': 'Lève-toi.',
  'mission.wake_without_phone.instruction.2': "Bois de l'eau.",
  'mission.wake_without_phone.instruction.3': 'Respire calmement.',
  'mission.mental_preparation.title': 'Préparation mentale',
  'mission.mental_preparation.objective': "Choisir ton état d'esprit avant de subir la journée.",
  'mission.mental_preparation.instruction.0': 'Respire lentement pendant une minute.',
  'mission.mental_preparation.instruction.1': 'Identifie ta mission essentielle.',
  'mission.mental_preparation.instruction.2': "Choisis l'attitude que tu veux incarner.",
  'mission.sport_session.title': 'Séance de sport',
  'mission.sport_session.objective': 'Activer le corps et renforcer la discipline.',
  'mission.sport_session.instruction.0': 'Prépare ton équipement.',
  'mission.sport_session.instruction.1': 'Démarre la mission pour voir la séance détaillée du jour.',
  'mission.sport_session.instruction.2': 'Adapte, remplace ou ignore chaque exercice selon ton état.',
  'mission.shower_recovery.title': 'Douche et récupération',
  'mission.shower_recovery.objective': "Revenir au calme après l'effort.",
  'mission.shower_recovery.instruction.0': 'Prends une douche.',
  'mission.shower_recovery.instruction.1': 'Hydrate-toi.',
  'mission.shower_recovery.instruction.2': 'Reviens à une respiration calme.',
  'mission.day_planning.title': 'Planification de la journée',
  'mission.day_planning.objective': 'Donner une direction claire à la journée.',
  'mission.day_planning.instruction.0': 'Choisis trois priorités maximum.',
  'mission.day_planning.instruction.1': 'Identifie la mission essentielle.',
  'mission.day_planning.instruction.2': 'Protège un créneau de travail profond.',
  'mission.deep_work.title': 'Travail profond',
  'mission.deep_work.objective': "Avancer sur la tâche ayant le plus d'impact.",
  'mission.deep_work.instruction.0': 'Coupe les notifications.',
  'mission.deep_work.instruction.1': 'Prépare uniquement les outils nécessaires.',
  'mission.deep_work.instruction.2': 'Travaille sans interruption pendant le créneau prévu.',
  'mission.growth_action.title': 'Action de croissance',
  'mission.growth_action.objective': 'Construire une capacité ou une opportunité future.',
  'mission.growth_action.instruction.0': 'Apprends, crée, prospecte ou améliore un système.',
  'mission.growth_action.instruction.1': 'Choisis une action concrète et mesurable.',
  'mission.conscious_relationship.title': 'Relation consciente',
  'mission.conscious_relationship.objective': 'Entretenir un lien important sans pression.',
  'mission.conscious_relationship.instruction.0': 'Offre une attention sincère.',
  'mission.conscious_relationship.instruction.1': 'Écoute ou envoie un message simple.',
  'mission.conscious_relationship.instruction.2': 'Ne cherche pas de validation immédiate.',
  'mission.evening_review.title': 'Bilan du soir',
  'mission.evening_review.objective': 'Clore la journée avec lucidité.',
  'mission.evening_review.instruction.0': 'Identifie une victoire.',
  'mission.evening_review.instruction.1': 'Note un apprentissage.',
  'mission.evening_review.instruction.2': 'Choisis la priorité de demain.',
  'mission.bedtime_ritual.title': 'Rituel avant de dormir',
  'mission.bedtime_ritual.objective': 'Préparer le repos et couper avec la journée.',
  'mission.bedtime_ritual.instruction.0': 'Éloigne le téléphone.',
  'mission.bedtime_ritual.instruction.1': 'Respire lentement.',
  'mission.bedtime_ritual.instruction.2': 'Relâche la journée sans rumination.',
  'mission.drink_water.title': "Boire de l'eau",
  'mission.drink_water.objective': 'Maintenir le geste minimal même dans un jour difficile.',
  'mission.drink_water.instruction.0': "Bois un verre d'eau.",
  'mission.conscious_breathing.title': 'Respiration consciente',
  'mission.conscious_breathing.objective': "Retrouver un point d'ancrage stable.",
  'mission.conscious_breathing.instruction.0': 'Respire lentement pendant quelques minutes.',
  'mission.conscious_breathing.instruction.1': 'Ne cherche rien de plus.',
  // ── Menu actions secondaires ────────────────────────────────────────
  'menu.actions_for': 'Actions pour',

  // ── Réorganisation / drag ───────────────────────────────────────────
  'reorder.position': 'Position',
  'reorder.position_sur': 'sur',
  'reorder.drag': 'Déplacer',
  'reorder.drag_mission': 'Déplacer la mission',
  'reorder.monter_mission': 'Monter la mission',
  'reorder.descendre_mission': 'Descendre la mission',
} as const;

const en: TranslationCatalog = {
  'nav.today': 'Today',
  'nav.missions': 'Missions',
  'nav.progression': 'Progress',
  'nav.ressources': 'Resources',
  'nav.aria': 'Main navigation',
  'header.brand': 'ASCEND INVICTUS',
  'hero.greeting.morning': 'Good morning',
  'hero.greeting.afternoon': 'Good afternoon',
  'hero.greeting.evening': 'Good evening',
  'guidelines.label': "TODAY'S GUIDELINES",
  'guidelines.routines': '3 ESSENTIAL ROUTINES',
  'mode.selector.label': 'Day mode',
  'mode.selector.footer': 'The mode drives functional logic — never emotional impact.',
  'mode.confirm': 'Confirm mode',
  'mode.pending': 'Saving…',
  'mode.cancel': 'Cancel',
  'mode.changer': 'Change mode',
  'mode.label': 'MODE',
  'mode.normal': 'Normal',
  'mode.elevation': 'Elevation',
  'mode.charge': 'Charge',
  'mode.difficile': 'Difficult',
  'mode.voyage': 'Voyage',
  'mode.transition': 'Transition',
  'mode.moment_decisif': 'Decisive moment',
  'mode.imprevu': 'Unexpected',
  'mode.recuperation': 'Recovery',
  'mode.normal.intention': 'Clarity. Discipline. Mastery.',
  'mode.charge.intention': 'Reduce to the necessary, without neglecting what matters.',
  'mode.voyage.intention': 'Stay connected to what matters, wherever you are.',
  'mode.recuperation.intention': 'Rebuild without guilt.',
  'mode.difficile.intention': 'Get through. Just get through.',
  'mode.elevation.intention': 'Deepen, do not accumulate.',
  'mode.moment_decisif.intention': 'Everything that is not the decision can wait.',
  'mode.imprevu.intention': 'What matters fits in one sentence.',
  'mode.transition.intention': 'Continuity before mastery.',
  'mode.consequence.normal': 'The full routine remains active, without restrictions.',
  'mode.consequence.busy': 'Only required missions and the essential optional mission will count toward progress. Sport becomes a short session and the review a shortened format. Nothing is removed: the rest of the routine remains visible.',
  'mode.consequence.travel': 'Schedules stay flexible and sport becomes equipment-free movement. The full routine remains active.',
  'mode.consequence.recovery': 'Sport becomes active recovery, without high intensity. The full routine remains active.',
  'mode.consequence.difficult': 'Only water, breathing, ten minutes of movement, one essential mission and a short review will count toward progress. No existing mission is removed.',
  'mode.consequence.elevation': 'The full routine remains active, at maximum standard: elevation is about deepening the gesture, not multiplying gestures.',
  'mode.consequence.moment_decisif': 'Only required missions and the essential optional mission will count toward progress. Everything that is not the decision can wait.',
  'mode.consequence.imprevu': 'Only required missions and the essential optional mission will count toward progress. The day has changed shape; what matters is kept.',
  'mode.consequence.transition': 'The full routine remains active, at low standard. Between two regimes, continuity matters more than immediate mastery.',
  // 9 mode thoughts (Hero mantra)
  'mode.normal.thought': '"Mastery begins with choosing what deserves your energy."',
  'mode.charge.thought': '"What is not essential has no place in your day."',
  'mode.voyage.thought': '"The landmarks change; the direction remains."',
  'mode.recuperation.thought': '"Recovery is part of progression."',
  'mode.difficile.thought': '"A difficult day does not demand perfect conduct, but just conduct."',
  'mode.elevation.thought': '"To elevate is to deepen the gesture, not to multiply gestures."',
  'mode.moment_decisif.thought': '"Prepare what depends on you, then enter fully into the moment."',
  'mode.imprevu.thought': '"Accept the change of plan without abandoning your direction."',
  'mode.transition.thought': '"Do not immediately demand mastery from a life that has just changed."',
  // 9 modes × 3 routines
  'mode.normal.routine.0': 'Start with intention',
  'mode.normal.routine.1': 'Complete the essential action',
  'mode.normal.routine.2': 'Finish with clarity',
  'mode.charge.routine.0': 'Reduce to the bare minimum',
  'mode.charge.routine.1': 'Protect the essential block',
  'mode.charge.routine.2': 'Reject unnecessary distractions',
  'mode.voyage.routine.0': 'Keep an anchor point',
  'mode.voyage.routine.1': 'Adapt without abandoning',
  'mode.voyage.routine.2': 'Integrate the travel experience',
  'mode.recuperation.routine.0': 'Reduce intensity',
  'mode.recuperation.routine.1': 'Restore the body',
  'mode.recuperation.routine.2': 'Prepare for return to balance',
  'mode.difficile.routine.0': 'Keep only the essential',
  'mode.difficile.routine.1': 'Advance one step at a time',
  'mode.difficile.routine.2': 'Preserve mastery until evening',
  'mode.elevation.routine.0': 'Choose the domain to elevate',
  'mode.elevation.routine.1': 'Execute the demanding gesture',
  'mode.elevation.routine.2': 'Measure progress with humility',
  'mode.moment_decisif.routine.0': 'Clarify the intention',
  'mode.moment_decisif.routine.1': 'Prepare the decisive gesture',
  'mode.moment_decisif.routine.2': 'Eliminate all unnecessary distractions',
  'mode.imprevu.routine.0': 'Secure the essential',
  'mode.imprevu.routine.1': 'Recompose the day',
  'mode.imprevu.routine.2': 'Abandon the non-essential',
  'mode.transition.routine.0': 'Accept the new rhythm',
  'mode.transition.routine.1': 'Install a first anchor',
  'mode.transition.routine.2': 'Take one foundational action',
  'state.selector.label': 'Emotional state',
  'state.clear': 'No state declared',
  'state.clear.short': 'NO STATE',
  'state.confirm': 'Confirm state',
  'state.pending': 'Saving…',
  'state.cancel': 'Cancel',
  'state.declarer': 'Declare a state',
  'state.label': 'STATE',
  'family.rayonnement': 'RADIANCE',
  'family.serenite': 'SERENITY',
  'family.pression_peur': 'PRESSURE / FEAR',
  'family.friction': 'FRICTION',
  'family.vulnerabilite': 'VULNERABILITY',
  'family.reparation': 'REPAIR',
  'family.recentrage': 'RECENTERING',
  'family.coup_dur': 'HARD TIMES',
  // Intentions
  'family.rayonnement.intention': 'Welcome momentum without losing your axis.',
  'family.serenite.intention': 'Hold what is right, without forcing it.',
  'family.pression_peur.intention': 'Narrow the field to what depends on you.',
  'family.friction.intention': 'Let things settle before deciding.',
  'family.vulnerabilite.intention': 'Get through without judging yourself.',
  'family.reparation.intention': 'Repair without forcing.',
  'family.recentrage.intention': 'Find your axis before walking again.',
  'family.coup_dur.intention': 'Hold on, simply.',
  // Thoughts
  'family.rayonnement.thought': 'What goes well deserves the same attention as what resists.',
  'family.serenite.thought': 'Calm is not the absence of movement, it is the rightness of rhythm.',
  'family.pression_peur.thought': 'Fear shrinks the field. One thing at a time reopens it.',
  'family.friction.thought': 'Anger informs what matters. It does not decide.',
  'family.vulnerabilite.thought': 'What you are worth is not measured by the day you just lived.',
  'family.reparation.thought': 'Resting is also an act of discipline.',
  'family.recentrage.thought': 'Losing direction is not losing the path.',
  'family.coup_dur.thought': 'Holding on today is enough. The rest can wait until tomorrow.',
  // Routines
  'family.rayonnement.routine.0': 'Start with intention',
  'family.rayonnement.routine.1': 'Do the essential action',
  'family.rayonnement.routine.2': 'Finish with clarity',
  'family.serenite.routine.0': 'Start without haste',
  'family.serenite.routine.1': 'Do the essential action',
  'family.serenite.routine.2': 'Close the day in silence',
  'family.pression_peur.routine.0': 'Name what worries you',
  'family.pression_peur.routine.1': 'Do the one thing that depends on you',
  'family.pression_peur.routine.2': 'Set the day down before night',
  'family.friction.routine.0': 'Defer the reaction',
  'family.friction.routine.1': 'Do the essential action',
  'family.friction.routine.2': 'Revisit the topic cold',
  'family.vulnerabilite.routine.0': 'Start small',
  'family.vulnerabilite.routine.1': 'Do one useful thing',
  'family.vulnerabilite.routine.2': 'Finish without a review',
  'family.reparation.routine.0': 'Get up without demands',
  'family.reparation.routine.1': 'Move gently',
  'family.reparation.routine.2': 'Go to bed earlier',
  'family.recentrage.routine.0': 'Write what has changed',
  'family.recentrage.routine.1': 'Do the simplest action',
  'family.recentrage.routine.2': 'Decide nothing tonight',
  'family.coup_dur.routine.0': 'Drink, eat, sleep',
  'family.coup_dur.routine.1': 'Do one thing',
  'family.coup_dur.routine.2': 'Tell one person',
  'state.joie': 'Joy',
  'state.accomplissement': 'Accomplishment',
  'state.serenite': 'Serenity',
  'state.sous_pression': 'Under pressure',
  'state.stress': 'Stress',
  'state.anxiete': 'Anxiety',
  'state.inquietude': 'Worry',
  'state.incertitude': 'Uncertainty',
  'state.apprehension': 'Apprehension',
  'state.colere': 'Anger',
  'state.frustration': 'Frustration',
  'state.irritation': 'Irritation',
  'state.impatience': 'Impatience',
  'state.injustice': 'Injustice',
  'state.conflit_mineur': 'Minor conflict',
  'state.solitude': 'Solitude',
  'state.rejet': 'Rejection',
  'state.honte': 'Shame',
  'state.culpabilite': 'Guilt',
  'state.doute_profond': 'Deep doubt',
  'state.insuffisance': 'Feeling of inadequacy',
  'state.malade': 'Sick',
  'state.epuise': 'Exhausted',
  'state.convalescence': 'Convalescence',
  'state.baisse_physique': 'Significant physical low',
  'state.echec': 'Failure',
  'state.deception': 'Disappointment',
  'state.perte_de_reperes': 'Loss of bearings',
  'state.confusion': 'Confusion',
  'state.transition_interieure': 'Inner transition',
  'state.doute_direction': 'Doubt about direction',
  'state.rupture': 'Breakup or major emotional conflict',
  'state.deuil': 'Grief or loss',
  'state.moral_bas': 'Very low morale',
  'state.mauvaise_nouvelle': 'Deep bad news',
  'state.choc_personnel': 'Major personal shock',
  'missions.label': "Today's missions",
  'missions.reorganiser': 'Reorder',
  'missions.enregistrer_ordre': 'Save order',
  'missions.ordre_enregistre': 'Order saved for today.',
  'missions.ordre_enregistre_futur': 'Order saved for today and upcoming days.',
  'missions.reorg_annulee': 'Reordering cancelled.',
  'missions.reorg_active': 'Reordering mode enabled.',
  'missions.deplacee': 'Mission moved to position',
  'missions.postpone_title': 'Postpone mission',
  'missions.postpone_confirm': 'Confirm postponement',
  'missions.skip_title': 'Skip this mission?',
  'missions.skip_confirm': 'Skip today',
  'missions.skip_body': 'The mission will remain visible in history without being rescheduled. No judgment: you can carry on with what matters.',
  'missions.reorder_scope_today': 'Today only',
  'missions.reorder_scope_future': 'Today and upcoming days',
  'missions.reorder_scope_legend': 'Scope of the new order',
  'missions.postpone_legend': 'Choose a future date',
  'missions.postpone_tomorrow': 'Tomorrow',
  'missions.postpone_2days': 'In 2 days',
  'missions.postpone_custom': 'Choose a date',
  'missions.postpone_custom_aria': 'Custom postponement date',
  'missions.empty_title': 'Free day',
  'missions.empty_message': 'No active missions planned for today.',
  'missions.out_of_mode': 'Outside current mode: this mission remains available but does not count toward today\'s progress.',
  'action.start': 'Start',
  'action.complete': 'Complete',
  'action.complete_direct': 'Complete directly',
  'action.reset': 'Reset to pending',
  'action.reactivate': 'Reactivate',
  'action.skip': 'Skip',
  'action.postpone': 'Postpone',
  'action.monter': 'Move up',
  'action.descendre': 'Move down',
  'action.annuler': 'Cancel',
  'action.enregistrer': 'Save',
  'action.reorganiser': 'Reorder',
  'action.ouvrir': 'Open',
  'action.fermer': 'Close',
  'action.valider': 'Validate',
  'category.mental': 'Mental',
  'category.physique': 'Physical',
  'category.strategique': 'Strategic',
  'category.relationnel': 'Relational',
  'category.logistique': 'Logistics',
  'category.administratif': 'Administrative',
  'category.creation': 'Creation',
  'category.apprentissage': 'Learning',
  'category.sport': 'Sport',
  'category.reflexion': 'Reflection',
  'category.rituel': 'Ritual',
  'category.bien_etre': 'Well-being',
  'category.finance': 'Finance',
  'category.communication': 'Communication',
  // RoutineCategory-specific labels
  'category.health': 'Health',
  'category.work': 'Work',
  'category.organization': 'Organization',
  'category.growth': 'Growth',
  'category.emotion': 'Emotion',
  'category.relationship': 'Relationship',
  'category.journal': 'Journal',
  'category.custom': 'Personal',
  // Mission detail panel labels
  'mission.objective_label': 'Objective',
  'mission.instructions_label': 'To do',
  'mission.aria_label': 'mission {title}',
  'mission.order_aria': 'Mission {n}',
  'mission.actions_aria': 'Actions for {title}',
  'mission.out_of_mode': 'Out of mode',
  'mission.provisional': 'Provisional content',
  'mission.mode_note': 'Outside the current mode: this mission remains available but does not count toward daily progress.',
  'priority.essentiel': 'ESSENTIAL',
  'priority.recommande': 'RECOMMENDED',
  'status.done': 'DONE',
  'status.deferred': 'DEFERRED',
  'status.postponed_label': 'Postponed to',
  'status.postponed': 'Postponed',
  'status.completed': 'Completed',
  'status.pending': 'To do',
  'status.in_progress': 'In progress',
  'status.skipped': 'Skipped',
  'badge.out_of_mode': 'Outside mode',
  'feedback.mission_started': 'Mission started.',
  'feedback.mission_completed': 'Mission completed.',
  'feedback.mission_reset': 'Mission reset to pending.',
  'feedback.mission_skipped': 'Mission skipped for today.',
  'feedback.mission_postponed': 'Mission postponed to',
  'feedback.adaptation_applied': 'Adaptation applied for today.',
  'feedback.adaptation_kept': 'Current order kept.',
  'feedback.reorg_cancelled': 'Reordering cancelled.',
  'progress.label': 'Daily progress',
  'progress.aria': 'Progress {percent}%',
  'progress.message.idle': 'One mission is enough to start the day.',
  'progress.message.launched': 'You have set things in motion.',
  'progress.message.continuing': 'Stay with what matters.',
  'progress.message.advancing': 'The day is moving forward.',
  'progress.message.solid': 'Solid day.',
  'progress.message.ascended': 'Ascension accomplished.',
  'sport.label': 'Sport',
  'sport.empty': 'No workout planned today.',
  'sport.loading': 'Loading workout…',
  'sport.safety': 'Stop the exercise if you feel unusual pain, dizziness, malaise, or unusual shortness of breath.',
  'sport.status.pending': 'To do',
  'sport.status.in_progress': 'In progress',
  'sport.status.completed': 'Completed',
  'sport.status.reduced': 'Reduced',
  'sport.exercise.complete': 'Complete',
  'sport.exercise.replace': 'Replace',
  'sport.exercise.skip': 'Skip',
  'sport.exercise.status.pending': 'To do',
  'sport.exercise.status.completed': 'Done',
  'sport.exercise.status.skipped': 'Skipped',
  'sport.exercise.status.replaced': 'Replaced',
  'sport.weight_label': 'Chosen weight (optional)',
  'sport.reduce': 'Reduce session',
  'sport.units.reps': 'repetitions',
  'sport.units.minutes': 'minutes',
  'sport.units.seconds': 'seconds',
  'sport.reduce_aria': 'Reduce the workout session',
  'sport.weight_aria': 'Weight selected for {name}',
  'sport.complete_aria': 'Complete {name}',
  'sport.replace_aria': 'Replace {name}',
  'sport.skip_aria': 'Skip {name}',
  'exercise.treadmill-walk.name': 'Brisk treadmill walk',
  'exercise.treadmill-walk.instructions': 'Walk at a sustained pace without forcing your speed.',
  'exercise.stationary-bike.name': 'Stationary bike',
  'exercise.stationary-bike.instructions': 'Pedal at a steady cadence, moderate resistance.',
  'exercise.bench-press.name': 'Bench press',
  'exercise.bench-press.instructions': 'Choose a weight you can control through the full range of motion.',
  'exercise.dumbbell-row.name': 'Dumbbell row',
  'exercise.dumbbell-row.instructions': 'Flat back, pull the dumbbell toward your hip without jerking.',
  'exercise.goblet-squat.name': 'Goblet squat',
  'exercise.goblet-squat.instructions': 'Lower until your thighs are parallel to the floor if possible.',
  'exercise.dumbbell-shoulder-press.name': 'Dumbbell shoulder press',
  'exercise.dumbbell-shoulder-press.instructions': 'Press the dumbbells overhead without arching your back.',
  'exercise.push-ups.name': 'Push-ups',
  'exercise.push-ups.instructions': 'Knees on the floor if needed, full range of motion.',
  'exercise.bodyweight-squats.name': 'Bodyweight squats',
  'exercise.bodyweight-squats.instructions': 'Lower to your comfortable range, steady rhythm.',
  'exercise.jumping-jacks.name': 'Jumping jacks',
  'exercise.jumping-jacks.instructions': 'Moderate pace, steady breathing.',
  'exercise.plank.name': 'Plank',
  'exercise.plank.instructions': 'Body aligned, continuous breathing.',
  'exercise.guided-stretch.name': 'Guided stretching',
  'exercise.guided-stretch.instructions': 'Stretch each muscle group without jerking or pain.',
  'exercise.breathing-walk.name': 'Breathing walk',
  'exercise.breathing-walk.instructions': 'Walk at a comfortable pace while breathing slowly.',
  'inspiration.label': 'Stoic inspiration',
  'inspiration.empty': 'No thought available today.',
  'inspiration.loading': 'Loading…',
  'guidance.label': 'Adaptive guidance',
  'guidance.empty': 'No suggestion today.',
  'guidance.accept': 'Accept',
  'guidance.accept.aria': 'Accept adaptation suggestion',
  'guidance.reject': 'Keep current order',
  'guidance.reject.aria': 'Keep current order',
  'guidance.accepted': '✓ Adaptation applied',
  'guidance.rejected': 'Current order kept',
  'guidance.summary.abstain_default': 'No adaptation suggested today.',
  'guidance.summary.abstain_ags_disabled': 'No adaptation suggested today.',
  'guidance.summary.abstain_observation_failed': 'No adaptation suggested today.',
  'guidance.summary.respect_accepted': 'Adaptation already accepted for today.',
  'guidance.summary.respect_rejected': 'Adaptation already declined for today.',
  'guidance.summary.propose': 'Reorganize missions?',
  'guidance.reason.abstain_default': 'The current order is already suitable.',
  'guidance.reason.abstain_ags_disabled': 'Adaptation suggestions are disabled.',
  'guidance.reason.abstain_observation_failed': 'Today\'s context could not be established.',
  'guidance.reason.respect_accepted': 'Adaptation already accepted.',
  'guidance.reason.respect_rejected': 'Adaptation already declined.',
  'guidance.reason.propose': 'A reorganization is proposed.',
  'guidance.reason.with_rule': '{mode} mode: {rule}.',
  'guidance.rule.required_first': 'required missions first',
  'guidance.rule.active_for_mode_first': 'travel-adapted missions first',
  'guidance.rule.essential_first': 'the essential mission first',
  'guidance.rule.essential_then_required': 'the essential then required missions first',
  'history.title': 'History',
  'history.empty': 'No recorded days.',
  'history.loading': 'Opening history…',
  'history.error': 'Could not load history.',
  'history.eyebrow': '{count} recent days',
  'history.description': 'Look at the path you have traveled, without judgment.',
  'history.empty_title': 'The path begins here',
  'history.empty_message': 'Your first day will appear as soon as you open Today.',
  'history.list': 'List',
  'history.calendar': 'Calendar',
  'history.completed': 'Completed',
  'history.open': 'Open',
  'history.view_aria': 'History view',
  'history.section_aria': 'Recent days',
  'history.progress_aria': 'Progress of {date}',
  'history.detail_loading': 'Opening the day…',
  'history.back': 'Back to history',
  'history.readonly': 'Read-only',
  'history.day_completed': 'Day completed',
  'history.day_open': 'Day open',
  'history.detail_progress_aria': 'Progress for this day',
  'history.progress_label': 'Historical progress',
  'history.missions_completed': '{completed} of {active} missions completed',
  'history.readonly_missions_aria': 'Read-only missions',
  'settings.title': 'Settings',
  'settings.profile': 'Profile',
  'settings.profile_description': 'This information stays on your device.',
  'settings.first_name': 'First name',
  'settings.first_name_placeholder': 'Your first name',
  'settings.language': 'Language',
  'settings.language_description': 'Choose the interface language. The change is immediate and persistent.',
  'settings.language.fr': 'Français',
  'settings.language.en': 'English',
  'settings.language.es': 'Español',
  'data.title': 'Data & privacy',
  'data.ownership_eyebrow': 'Your data belongs to you',
  'data.description': 'Export, restore or delete all of your local data.',
  'data.export': 'Export my data',
  'data.exporting': 'Exporting…',
  'data.export_description': 'Download a complete copy of your missions, days, journals and workout sessions in JSON format. This file is not encrypted: keep it in a safe place.',
  'data.import': 'Import a backup',
  'data.import_description': 'Replaces your current data entirely with the contents of a previously exported file. A safety backup of the current state is made before importing.',
  'data.choose_file': 'Choose a file',
  'data.choose_file_aria': 'Choose a backup file to import',
  'data.delete': 'Delete all my data',
  'data.delete_description': 'Permanently deletes all of your missions, days, journals, sessions and local settings. This action is irreversible.',
  'data.delete_title': 'Delete all data?',
  'data.delete_final': 'Delete permanently',
  'data.import_confirm': 'Confirm import',
  'data.import_replace': 'Import and replace',
  'data.import_preview_intro': 'This file contains:',
  'data.import_preview_days': '{count} days',
  'data.import_preview_missions': '{count} missions',
  'data.import_preview_journal': '{count} journal entries',
  'data.import_preview_workouts': '{count} workout sessions',
  'data.import_warning': 'All your current data will be replaced. This action is irreversible.',
  'data.export_success': 'Data exported successfully.',
  'data.import_success': 'Import complete. Your data has been replaced by the file\'s content. A backup of your previous state has just been downloaded.',
  'data.delete_confirm': 'Confirm deletion',
  'data.delete_warning': 'All of your missions, days, journals, sessions and local settings will be permanently deleted. This action is irreversible.',
  'data.delete_success': 'All your data has been deleted. A new day awaits you.',
  'data.import_preview': 'Import preview',
  'states.loading': 'Loading the day…',
  'states.storage_unavailable': 'Local storage unavailable',
  'states.prep_interrupted': 'Local preparation interrupted',
  'states.prep_failed': 'Could not prepare the day',
  'states.diagnostic': 'Technical diagnostic',
  'states.empty_eyebrow': 'Space available',
  'calendar.aria': 'Activity calendar',
  'calendar.day_aria': '{date} · {percent}% progress',
  'calendar.less': 'Less',
  'calendar.more': 'More',
  'calendar.sun': 'S',
  'calendar.mon': 'M',
  'calendar.tue': 'T',
  'calendar.wed': 'W',
  'calendar.thu': 'T',
  'calendar.fri': 'F',
  'calendar.sat': 'S',
  'collapsible.expand': 'Expand',
  'collapsible.collapse': 'Collapse',
  'error.loading': 'The day could not be loaded.',
  'error.retry': 'Retry',
  'error.generic': 'An error occurred.',
  'error.not_found': 'Page not found.',
  'architecture.title': 'Architecture',
  'version.label': 'Application version',
  'version.field': 'Version',
  'version.commit': 'Commit',
  'version.build_date': 'Build date',
  // ── Missions (seed MVP) ─────────────────────────────────────────────
  'mission.wake_without_phone.title': 'Wake up without your phone',
  'mission.wake_without_phone.objective': 'Start the day without distraction.',
  'mission.wake_without_phone.instruction.0': 'Do not check notifications.',
  'mission.wake_without_phone.instruction.1': 'Get up.',
  'mission.wake_without_phone.instruction.2': 'Drink water.',
  'mission.wake_without_phone.instruction.3': 'Breathe calmly.',
  'mission.mental_preparation.title': 'Mental preparation',
  'mission.mental_preparation.objective': 'Choose your mindset before the day happens to you.',
  'mission.mental_preparation.instruction.0': 'Breathe slowly for a minute.',
  'mission.mental_preparation.instruction.1': 'Identify your essential mission.',
  'mission.mental_preparation.instruction.2': 'Choose the attitude you want to embody.',
  'mission.sport_session.title': 'Workout session',
  'mission.sport_session.objective': 'Activate the body and reinforce discipline.',
  'mission.sport_session.instruction.0': 'Prepare your equipment.',
  'mission.sport_session.instruction.1': 'Start the mission to see the detailed workout of the day.',
  'mission.sport_session.instruction.2': 'Adapt, replace or skip each exercise based on how you feel.',
  'mission.shower_recovery.title': 'Shower and recovery',
  'mission.shower_recovery.objective': 'Return to calm after exertion.',
  'mission.shower_recovery.instruction.0': 'Take a shower.',
  'mission.shower_recovery.instruction.1': 'Hydrate.',
  'mission.shower_recovery.instruction.2': 'Return to calm breathing.',
  'mission.day_planning.title': 'Day planning',
  'mission.day_planning.objective': 'Give the day a clear direction.',
  'mission.day_planning.instruction.0': 'Choose three priorities at most.',
  'mission.day_planning.instruction.1': 'Identify the essential mission.',
  'mission.day_planning.instruction.2': 'Protect a deep work time slot.',
  'mission.deep_work.title': 'Deep work',
  'mission.deep_work.objective': 'Make progress on the highest-impact task.',
  'mission.deep_work.instruction.0': 'Turn off notifications.',
  'mission.deep_work.instruction.1': 'Prepare only the necessary tools.',
  'mission.deep_work.instruction.2': 'Work without interruption during the planned time block.',
  'mission.growth_action.title': 'Growth action',
  'mission.growth_action.objective': 'Build a capability or a future opportunity.',
  'mission.growth_action.instruction.0': 'Learn, create, prospect, or improve a system.',
  'mission.growth_action.instruction.1': 'Choose a concrete and measurable action.',
  'mission.conscious_relationship.title': 'Conscious relationship',
  'mission.conscious_relationship.objective': 'Nurture an important connection without pressure.',
  'mission.conscious_relationship.instruction.0': 'Offer sincere attention.',
  'mission.conscious_relationship.instruction.1': 'Listen or send a simple message.',
  'mission.conscious_relationship.instruction.2': 'Do not seek immediate validation.',
  'mission.evening_review.title': 'Evening review',
  'mission.evening_review.objective': 'Close the day with clarity.',
  'mission.evening_review.instruction.0': 'Identify one win.',
  'mission.evening_review.instruction.1': 'Note one lesson learned.',
  'mission.evening_review.instruction.2': 'Choose the priority for tomorrow.',
  'mission.bedtime_ritual.title': 'Bedtime ritual',
  'mission.bedtime_ritual.objective': 'Prepare for rest and disconnect from the day.',
  'mission.bedtime_ritual.instruction.0': 'Put the phone away.',
  'mission.bedtime_ritual.instruction.1': 'Breathe slowly.',
  'mission.bedtime_ritual.instruction.2': 'Let go of the day without rumination.',
  'mission.drink_water.title': 'Drink water',
  'mission.drink_water.objective': 'Maintain the minimal gesture even on a difficult day.',
  'mission.drink_water.instruction.0': 'Drink a glass of water.',
  'mission.conscious_breathing.title': 'Conscious breathing',
  'mission.conscious_breathing.objective': 'Find a stable anchor point.',
  'mission.conscious_breathing.instruction.0': 'Breathe slowly for a few minutes.',
  'mission.conscious_breathing.instruction.1': 'Do not seek anything more.',
  'menu.actions_for': 'Actions for',
  'reorder.position': 'Position',
  'reorder.position_sur': 'of',
  'reorder.drag': 'Move',
  'reorder.drag_mission': 'Move mission',
  'reorder.monter_mission': 'Move mission up',
  'reorder.descendre_mission': 'Move mission down',
} as const;

const es: TranslationCatalog = {
  'nav.today': 'Hoy',
  'nav.missions': 'Misiones',
  'nav.progression': 'Progreso',
  'nav.ressources': 'Recursos',
  'nav.aria': 'Navegación principal',
  'header.brand': 'ASCEND INVICTUS',
  'hero.greeting.morning': 'Buenos días',
  'hero.greeting.afternoon': 'Buenas tardes',
  'hero.greeting.evening': 'Buenas noches',
  'guidelines.label': 'LÍNEAS DEL DÍA',
  'guidelines.routines': '3 RUTINAS ESENCIALES',
  'mode.selector.label': 'Modo del día',
  'mode.selector.footer': 'El modo define la lógica funcional — nunca el impacto emocional.',
  'mode.confirm': 'Confirmar modo',
  'mode.pending': 'Guardando…',
  'mode.cancel': 'Cancelar',
  'mode.changer': 'Cambiar modo',
  'mode.label': 'MODO',
  'mode.normal': 'Normal',
  'mode.elevation': 'Elevación',
  'mode.charge': 'Carga',
  'mode.difficile': 'Difícil',
  'mode.voyage': 'Viaje',
  'mode.transition': 'Transición',
  'mode.moment_decisif': 'Momento decisivo',
  'mode.imprevu': 'Imprevisto',
  'mode.recuperation': 'Recuperación',
  'mode.normal.intention': 'Claridad. Disciplina. Dominio.',
  'mode.charge.intention': 'Reducir a lo necesario, sin descuidar lo esencial.',
  'mode.voyage.intention': 'Permanecer conectado a lo esencial, donde sea que estés.',
  'mode.recuperation.intention': 'Reconstruir sin culpa.',
  'mode.difficile.intention': 'Atravesar. Solo atravesar.',
  'mode.elevation.intention': 'Profundizar, no acumular.',
  'mode.moment_decisif.intention': 'Todo lo que no es la decisión puede esperar.',
  'mode.imprevu.intention': 'Lo esencial cabe en una frase.',
  'mode.transition.intention': 'La continuidad antes que el dominio.',
  'mode.consequence.normal': 'La rutina completa permanece activa, sin restricciones.',
  'mode.consequence.busy': 'Solo las misiones obligatorias y la misión facultativa esencial contarán para el progreso. El deporte se convierte en una sesión corta y el balance en un formato reducido. Nada se elimina: el resto de la rutina sigue visible.',
  'mode.consequence.travel': 'Los horarios permanecen flexibles y el deporte se convierte en movimiento sin equipamiento. La rutina completa permanece activa.',
  'mode.consequence.recovery': 'El deporte se convierte en recuperación activa, sin alta intensidad. La rutina completa permanece activa.',
  'mode.consequence.difficult': 'Solo agua, respiración, diez minutos de movimiento, una misión esencial y un balance corto contarán para el progreso. Ninguna misión existente se elimina.',
  'mode.consequence.elevation': 'La rutina completa permanece activa, con exigencia máxima: elevarse consiste en profundizar el gesto, no en multiplicar los gestos.',
  'mode.consequence.moment_decisif': 'Solo las misiones obligatorias y la misión facultativa esencial contarán para el progreso. Todo lo que no es la decisión puede esperar.',
  'mode.consequence.imprevu': 'Solo las misiones obligatorias y la misión facultativa esencial contarán para el progreso. La jornada ha cambiado de forma; lo esencial se conserva.',
  'mode.consequence.transition': 'La rutina completa permanece activa, con exigencia baja. Entre dos regímenes, la continuidad prima sobre el dominio inmediato.',
  // 9 pensamientos de modo (Hero mantra)
  'mode.normal.thought': '«El dominio comienza por elegir lo que merece tu energía.»',
  'mode.charge.thought': '«Lo que no es esencial no debe entrar en tu jornada.»',
  'mode.voyage.thought': '«Los puntos de referencia cambian; la dirección permanece.»',
  'mode.recuperation.thought': '«La recuperación es parte de la progresión.»',
  'mode.difficile.thought': '«Un día difícil no exige una conducta perfecta, sino una conducta justa.»',
  'mode.elevation.thought': '«Elevarse consiste en profundizar el gesto, no en multiplicar los gestos.»',
  'mode.moment_decisif.thought': '«Prepara lo que depende de ti, luego entra plenamente en el momento.»',
  'mode.imprevu.thought': '«Acepta el cambio de plan sin abandonar tu dirección.»',
  'mode.transition.thought': '«No exijas inmediatamente el dominio de una vida que acaba de cambiar.»',
  // 9 modes × 3 routines
  'mode.normal.routine.0': 'Comenzar con intención',
  'mode.normal.routine.1': 'Realizar la acción esencial',
  'mode.normal.routine.2': 'Terminar con claridad',
  'mode.charge.routine.0': 'Reducir a lo estrictamente necesario',
  'mode.charge.routine.1': 'Proteger el bloque esencial',
  'mode.charge.routine.2': 'Rechazar distracciones innecesarias',
  'mode.voyage.routine.0': 'Mantener un punto de anclaje',
  'mode.voyage.routine.1': 'Adaptarse sin abandonar',
  'mode.voyage.routine.2': 'Integrar la experiencia del viaje',
  'mode.recuperation.routine.0': 'Reducir la intensidad',
  'mode.recuperation.routine.1': 'Restaurar el cuerpo',
  'mode.recuperation.routine.2': 'Preparar el regreso al equilibrio',
  'mode.difficile.routine.0': 'Conservar solo lo esencial',
  'mode.difficile.routine.1': 'Avanzar paso a paso',
  'mode.difficile.routine.2': 'Preservar el dominio hasta la noche',
  'mode.elevation.routine.0': 'Elegir el dominio a elevar',
  'mode.elevation.routine.1': 'Ejecutar el gesto exigente',
  'mode.elevation.routine.2': 'Medir el progreso con humildad',
  'mode.moment_decisif.routine.0': 'Clarificar la intención',
  'mode.moment_decisif.routine.1': 'Preparar el gesto decisivo',
  'mode.moment_decisif.routine.2': 'Eliminar toda distracción innecesaria',
  'mode.imprevu.routine.0': 'Asegurar lo esencial',
  'mode.imprevu.routine.1': 'Recomponer la jornada',
  'mode.imprevu.routine.2': 'Abandonar lo no indispensable',
  'mode.transition.routine.0': 'Aceptar el nuevo ritmo',
  'mode.transition.routine.1': 'Fijar un primer anclaje',
  'mode.transition.routine.2': 'Realizar una acción fundacional',
  'state.selector.label': 'Estado emocional',
  'state.clear': 'Sin estado declarado',
  'state.clear.short': 'SIN ESTADO',
  'state.confirm': 'Confirmar estado',
  'state.pending': 'Guardando…',
  'state.cancel': 'Cancelar',
  'state.declarer': 'Declarar un estado',
  'state.label': 'ESTADO',
  'family.rayonnement': 'RESPLANDOR',
  'family.serenite': 'SERENIDAD',
  'family.pression_peur': 'PRESIÓN / MIEDO',
  'family.friction': 'FRICCIÓN',
  'family.vulnerabilite': 'VULNERABILIDAD',
  'family.reparation': 'REPARACIÓN',
  'family.recentrage': 'RECENTRADO',
  'family.coup_dur': 'GOLPE DURO',
  // Intenciones
  'family.rayonnement.intention': 'Acoger el impulso sin perder el eje.',
  'family.serenite.intention': 'Sostener lo que es justo, sin forzarlo.',
  'family.pression_peur.intention': 'Reducir el campo a lo que depende de ti.',
  'family.friction.intention': 'Dejar que las cosas se asienten antes de decidir.',
  'family.vulnerabilite.intention': 'Atravesar sin juzgarse.',
  'family.reparation.intention': 'Reparar sin forzar.',
  'family.recentrage.intention': 'Recuperar el eje antes de retomar la marcha.',
  'family.coup_dur.intention': 'Aguantar, simplemente.',
  // Pensamientos
  'family.rayonnement.thought': 'Lo que va bien merece la misma atención que lo que resiste.',
  'family.serenite.thought': 'La calma no es la ausencia de movimiento, es la justeza del ritmo.',
  'family.pression_peur.thought': 'El miedo encoge el campo. Una sola cosa a la vez lo reabre.',
  'family.friction.thought': 'La ira informa sobre lo que importa. No decide.',
  'family.vulnerabilite.thought': 'Lo que vales no se mide por el día que acabas de vivir.',
  'family.reparation.thought': 'Descansar es también un acto de disciplina.',
  'family.recentrage.thought': 'Perder la dirección no es perder el camino.',
  'family.coup_dur.thought': 'Aguantar hoy basta. El resto esperará a mañana.',
  // Rutinas
  'family.rayonnement.routine.0': 'Empezar con intención',
  'family.rayonnement.routine.1': 'Realizar la acción esencial',
  'family.rayonnement.routine.2': 'Terminar con claridad',
  'family.serenite.routine.0': 'Empezar sin prisa',
  'family.serenite.routine.1': 'Realizar la acción esencial',
  'family.serenite.routine.2': 'Cerrar el día en silencio',
  'family.pression_peur.routine.0': 'Nombrar lo que inquieta',
  'family.pression_peur.routine.1': 'Hacer lo único que depende de ti',
  'family.pression_peur.routine.2': 'Dejar el día antes de la noche',
  'family.friction.routine.0': 'Diferir la reacción',
  'family.friction.routine.1': 'Realizar la acción esencial',
  'family.friction.routine.2': 'Retomar el tema en frío',
  'family.vulnerabilite.routine.0': 'Empezar pequeño',
  'family.vulnerabilite.routine.1': 'Hacer una sola cosa útil',
  'family.vulnerabilite.routine.2': 'Terminar sin balance',
  'family.reparation.routine.0': 'Levantarse sin exigencias',
  'family.reparation.routine.1': 'Moverse suavemente',
  'family.reparation.routine.2': 'Acostarse más temprano',
  'family.recentrage.routine.0': 'Escribir lo que ha cambiado',
  'family.recentrage.routine.1': 'Hacer la acción más simple',
  'family.recentrage.routine.2': 'No decidir nada esta noche',
  'family.coup_dur.routine.0': 'Beber, comer, dormir',
  'family.coup_dur.routine.1': 'Hacer una sola cosa',
  'family.coup_dur.routine.2': 'Avisar a una persona',
  'state.joie': 'Alegría',
  'state.accomplissement': 'Logro',
  'state.serenite': 'Serenidad',
  'state.sous_pression': 'Bajo presión',
  'state.stress': 'Estrés',
  'state.anxiete': 'Ansiedad',
  'state.inquietude': 'Inquietud',
  'state.incertitude': 'Incertidumbre',
  'state.apprehension': 'Aprensión',
  'state.colere': 'Ira',
  'state.frustration': 'Frustración',
  'state.irritation': 'Irritación',
  'state.impatience': 'Impaciencia',
  'state.injustice': 'Injusticia',
  'state.conflit_mineur': 'Conflicto menor',
  'state.solitude': 'Soledad',
  'state.rejet': 'Rechazo',
  'state.honte': 'Vergüenza',
  'state.culpabilite': 'Culpa',
  'state.doute_profond': 'Duda profunda',
  'state.insuffisance': 'Sentimiento de insuficiencia',
  'state.malade': 'Enfermo',
  'state.epuise': 'Agotado',
  'state.convalescence': 'Convalecencia',
  'state.baisse_physique': 'Bajón físico importante',
  'state.echec': 'Fracaso',
  'state.deception': 'Decepción',
  'state.perte_de_reperes': 'Pérdida de referencias',
  'state.confusion': 'Confusión',
  'state.transition_interieure': 'Transición interior',
  'state.doute_direction': 'Duda sobre la dirección',
  'state.rupture': 'Ruptura o conflicto afectivo mayor',
  'state.deuil': 'Duelo o pérdida',
  'state.moral_bas': 'Moral muy bajo',
  'state.mauvaise_nouvelle': 'Mala noticia profunda',
  'state.choc_personnel': 'Choque personal importante',
  'missions.label': 'Misiones del día',
  'missions.reorganiser': 'Reorganizar',
  'missions.enregistrer_ordre': 'Guardar orden',
  'missions.ordre_enregistre': 'Orden guardado para hoy.',
  'missions.ordre_enregistre_futur': 'Orden guardado para hoy y los próximos días.',
  'missions.reorg_annulee': 'Reorganización cancelada.',
  'missions.reorg_active': 'Modo reorganización activado.',
  'missions.deplacee': 'Misión movida a la posición',
  'missions.postpone_title': 'Posponer misión',
  'missions.postpone_confirm': 'Confirmar aplazamiento',
  'missions.skip_title': '¿Ignorar esta misión?',
  'missions.skip_confirm': 'Ignorar hoy',
  'missions.skip_body': 'La misión seguirá visible en el historial sin ser reprogramada. Sin juicios: puedes continuar con lo esencial.',
  'missions.reorder_scope_today': 'Solo hoy',
  'missions.reorder_scope_future': 'Hoy y próximos días',
  'missions.reorder_scope_legend': 'Alcance del nuevo orden',
  'missions.postpone_legend': 'Elegir una fecha futura',
  'missions.postpone_tomorrow': 'Mañana',
  'missions.postpone_2days': 'En 2 días',
  'missions.postpone_custom': 'Elegir una fecha',
  'missions.postpone_custom_aria': 'Fecha de aplazamiento personalizada',
  'missions.empty_title': 'Día libre',
  'missions.empty_message': 'Ninguna misión activa prevista para hoy.',
  'missions.out_of_mode': 'Fuera del modo actual: esta misión sigue disponible pero no cuenta para el progreso de hoy.',
  'action.start': 'Iniciar',
  'action.complete': 'Terminar',
  'action.complete_direct': 'Terminar directamente',
  'action.reset': 'Restablecer',
  'action.reactivate': 'Reactivar',
  'action.skip': 'Ignorar',
  'action.postpone': 'Posponer',
  'action.monter': 'Subir',
  'action.descendre': 'Bajar',
  'action.annuler': 'Cancelar',
  'action.enregistrer': 'Guardar',
  'action.reorganiser': 'Reorganizar',
  'action.ouvrir': 'Abrir',
  'action.fermer': 'Cerrar',
  'action.valider': 'Validar',
  'category.mental': 'Mental',
  'category.physique': 'Físico',
  'category.strategique': 'Estratégico',
  'category.relationnel': 'Relacional',
  'category.logistique': 'Logística',
  'category.administratif': 'Administrativo',
  'category.creation': 'Creación',
  'category.apprentissage': 'Aprendizaje',
  'category.sport': 'Deporte',
  'category.reflexion': 'Reflexión',
  'category.rituel': 'Ritual',
  'category.bien_etre': 'Bienestar',
  'category.finance': 'Finanzas',
  'category.communication': 'Comunicación',
  // RoutineCategory-specific labels
  'category.health': 'Salud',
  'category.work': 'Trabajo',
  'category.organization': 'Organización',
  'category.growth': 'Crecimiento',
  'category.emotion': 'Emoción',
  'category.relationship': 'Relación',
  'category.journal': 'Diario',
  'category.custom': 'Personal',
  // Mission detail panel labels
  'mission.objective_label': 'Objetivo',
  'mission.instructions_label': 'Por hacer',
  'mission.aria_label': 'la misión {title}',
  'mission.order_aria': 'Misión {n}',
  'mission.actions_aria': 'Acciones para {title}',
  'mission.out_of_mode': 'Fuera de modo',
  'mission.provisional': 'Contenido provisional',
  'mission.mode_note': 'Fuera del modo actual: esta misión sigue disponible pero no cuenta para el progreso del día.',
  'priority.essentiel': 'ESENCIAL',
  'priority.recommande': 'RECOMENDADO',
  'status.done': 'HECHO',
  'status.deferred': 'APLAZADO',
  'status.postponed_label': 'Pos puesto al',
  'status.postponed': 'Pospuesta',
  'status.completed': 'Terminada',
  'status.pending': 'Por hacer',
  'status.in_progress': 'En curso',
  'status.skipped': 'Ignorada',
  'badge.out_of_mode': 'Fuera de modo',
  'feedback.mission_started': 'Misión iniciada.',
  'feedback.mission_completed': 'Misión terminada.',
  'feedback.mission_reset': 'Misión restablecida.',
  'feedback.mission_skipped': 'Misión ignorada por hoy.',
  'feedback.mission_postponed': 'Misión pospuesta al',
  'feedback.adaptation_applied': 'Adaptación aplicada para hoy.',
  'feedback.adaptation_kept': 'Orden actual conservado.',
  'feedback.reorg_cancelled': 'Reorganización cancelada.',
  'progress.label': 'Progreso diario',
  'progress.aria': 'Progreso {percent}%',
  'progress.message.idle': 'Una sola misión basta para lanzar la jornada.',
  'progress.message.launched': 'Has puesto las cosas en movimiento.',
  'progress.message.continuing': 'Sigue con lo esencial.',
  'progress.message.advancing': 'La jornada avanza.',
  'progress.message.solid': 'Jornada sólida.',
  'progress.message.ascended': 'Ascensión cumplida.',
  'sport.label': 'Deporte',
  'sport.empty': 'Ninguna sesión prevista hoy.',
  'sport.loading': 'Cargando sesión…',
  'sport.safety': 'Detén el ejercicio en caso de dolor anormal, mareo, malestar o dificultad para respirar inusual.',
  'sport.status.pending': 'Por hacer',
  'sport.status.in_progress': 'En curso',
  'sport.status.completed': 'Terminada',
  'sport.status.reduced': 'Reducida',
  'sport.exercise.complete': 'Terminar',
  'sport.exercise.replace': 'Reemplazar',
  'sport.exercise.skip': 'Ignorar',
  'sport.exercise.status.pending': 'Por hacer',
  'sport.exercise.status.completed': 'Hecho',
  'sport.exercise.status.skipped': 'Ignorado',
  'sport.exercise.status.replaced': 'Reemplazado',
  'sport.weight_label': 'Peso elegido (opcional)',
  'sport.reduce': 'Reducir sesión',
  'sport.units.reps': 'repeticiones',
  'sport.units.minutes': 'minutos',
  'sport.units.seconds': 'segundos',
  'sport.reduce_aria': 'Reducir la sesión de deporte',
  'sport.weight_aria': 'Peso elegido para {name}',
  'sport.complete_aria': 'Terminar {name}',
  'sport.replace_aria': 'Reemplazar {name}',
  'sport.skip_aria': 'Ignorar {name}',
  'exercise.treadmill-walk.name': 'Caminata rápida en cinta',
  'exercise.treadmill-walk.instructions': 'Camina a un ritmo sostenido, sin forzar la velocidad.',
  'exercise.stationary-bike.name': 'Bicicleta fija',
  'exercise.stationary-bike.instructions': 'Pedalea con una cadencia regular, resistencia moderada.',
  'exercise.bench-press.name': 'Press de banca',
  'exercise.bench-press.instructions': 'Elige un peso que puedas controlar en todo el recorrido.',
  'exercise.dumbbell-row.name': 'Remo con mancuerna',
  'exercise.dumbbell-row.instructions': 'Espalda plana, tira de la mancuerna hacia la cadera sin tirones.',
  'exercise.goblet-squat.name': 'Sentadilla goblet',
  'exercise.goblet-squat.instructions': 'Desciende hasta que los muslos estén paralelos al suelo si es posible.',
  'exercise.dumbbell-shoulder-press.name': 'Press de hombros con mancuernas',
  'exercise.dumbbell-shoulder-press.instructions': 'Empuja las mancuernas sobre la cabeza sin arquear la espalda.',
  'exercise.push-ups.name': 'Flexiones',
  'exercise.push-ups.instructions': 'Rodillas al suelo si es necesario, amplitud completa.',
  'exercise.bodyweight-squats.name': 'Sentadillas con peso corporal',
  'exercise.bodyweight-squats.instructions': 'Desciende hasta tu amplitud cómoda, ritmo regular.',
  'exercise.jumping-jacks.name': 'Saltos de tijera',
  'exercise.jumping-jacks.instructions': 'Ritmo moderado, respiración regular.',
  'exercise.plank.name': 'Plancha',
  'exercise.plank.instructions': 'Cuerpo alineado, respiración continua.',
  'exercise.guided-stretch.name': 'Estiramientos guiados',
  'exercise.guided-stretch.instructions': 'Estira cada grupo muscular sin tirones ni dolor.',
  'exercise.breathing-walk.name': 'Caminata respiratoria',
  'exercise.breathing-walk.instructions': 'Camina a un ritmo cómodo respirando lentamente.',
  'inspiration.label': 'Inspiración estoica',
  'inspiration.empty': 'Ningún pensamiento disponible hoy.',
  'inspiration.loading': 'Cargando…',
  'guidance.label': 'Guía adaptativa',
  'guidance.empty': 'Ninguna sugerencia hoy.',
  'guidance.accept': 'Aceptar',
  'guidance.accept.aria': 'Aceptar la sugerencia de adaptación',
  'guidance.reject': 'Mantener orden actual',
  'guidance.reject.aria': 'Mantener orden actual',
  'guidance.accepted': '✓ Adaptación aplicada',
  'guidance.rejected': 'Orden actual conservado',
  'guidance.summary.abstain_default': 'Ninguna adaptación propuesta hoy.',
  'guidance.summary.abstain_ags_disabled': 'Ninguna adaptación propuesta hoy.',
  'guidance.summary.abstain_observation_failed': 'Ninguna adaptación propuesta hoy.',
  'guidance.summary.respect_accepted': 'Adaptación ya aceptada para hoy.',
  'guidance.summary.respect_rejected': 'Adaptación ya rechazada para hoy.',
  'guidance.summary.propose': '¿Reorganizar las misiones?',
  'guidance.reason.abstain_default': 'El orden actual ya es adecuado.',
  'guidance.reason.abstain_ags_disabled': 'Las sugerencias de adaptación están desactivadas.',
  'guidance.reason.abstain_observation_failed': 'No se pudo establecer el contexto del día.',
  'guidance.reason.respect_accepted': 'Adaptación ya aceptada.',
  'guidance.reason.respect_rejected': 'Adaptación ya rechazada.',
  'guidance.reason.propose': 'Se propone una reorganización.',
  'guidance.reason.with_rule': 'Modo {mode}: {rule}.',
  'guidance.rule.required_first': 'misiones obligatorias primero',
  'guidance.rule.active_for_mode_first': 'misiones adaptadas al viaje primero',
  'guidance.rule.essential_first': 'la misión esencial primero',
  'guidance.rule.essential_then_required': 'la esencial y luego las obligatorias primero',
  'history.title': 'Historial',
  'history.empty': 'Ningún día registrado.',
  'history.loading': 'Abriendo historial…',
  'history.error': 'No se pudo cargar el historial.',
  'history.eyebrow': '{count} días recientes',
  'history.description': 'Mira el camino recorrido, sin juicios.',
  'history.empty_title': 'El camino comienza aquí',
  'history.empty_message': 'Tu primer día aparecerá en cuanto abras Hoy.',
  'history.list': 'Lista',
  'history.calendar': 'Calendario',
  'history.completed': 'Terminada',
  'history.open': 'Abierta',
  'history.view_aria': 'Vista del historial',
  'history.section_aria': 'Días recientes',
  'history.progress_aria': 'Progreso del {date}',
  'history.detail_loading': 'Abriendo el día…',
  'history.back': 'Volver al historial',
  'history.readonly': 'Solo lectura',
  'history.day_completed': 'Día terminado',
  'history.day_open': 'Día abierto',
  'history.detail_progress_aria': 'Progreso de este día',
  'history.progress_label': 'Progreso histórico',
  'history.missions_completed': '{completed} de {active} misiones terminadas',
  'history.readonly_missions_aria': 'Misiones de solo lectura',
  'settings.title': 'Ajustes',
  'settings.profile': 'Perfil',
  'settings.profile_description': 'Esta información permanece en tu dispositivo.',
  'settings.first_name': 'Nombre',
  'settings.first_name_placeholder': 'Tu nombre',
  'settings.language': 'Idioma',
  'settings.language_description': 'Elige el idioma de la interfaz. El cambio es inmediato y persistente.',
  'settings.language.fr': 'Français',
  'settings.language.en': 'English',
  'settings.language.es': 'Español',
  'data.title': 'Datos y privacidad',
  'data.ownership_eyebrow': 'Tus datos te pertenecen',
  'data.description': 'Exporta, restaura o elimina todos tus datos locales.',
  'data.export': 'Exportar mis datos',
  'data.exporting': 'Exportando…',
  'data.export_description': 'Descarga una copia completa de tus misiones, días, diarios y sesiones de deporte en formato JSON. Este archivo no está cifrado: guárdalo en un lugar seguro.',
  'data.import': 'Importar una copia de seguridad',
  'data.import_description': 'Reemplaza por completo tus datos actuales con el contenido de un archivo exportado anteriormente. Se realiza una copia de seguridad del estado actual antes de la importación.',
  'data.choose_file': 'Elegir un archivo',
  'data.choose_file_aria': 'Elige un archivo de copia de seguridad para importar',
  'data.delete': 'Eliminar todos mis datos',
  'data.delete_description': 'Elimina definitivamente todas tus misiones, días, diarios, sesiones y ajustes locales. Esta acción es irreversible.',
  'data.delete_title': '¿Eliminar todos los datos?',
  'data.delete_final': 'Eliminar definitivamente',
  'data.import_confirm': 'Confirmar importación',
  'data.import_replace': 'Importar y reemplazar',
  'data.import_preview_intro': 'Este archivo contiene:',
  'data.import_preview_days': '{count} días',
  'data.import_preview_missions': '{count} misiones',
  'data.import_preview_journal': '{count} entradas de diario',
  'data.import_preview_workouts': '{count} sesiones de deporte',
  'data.import_warning': 'Todos tus datos actuales serán reemplazados. Esta acción es irreversible.',
  'data.export_success': 'Datos exportados con éxito.',
  'data.import_success': 'Importación completada. Tus datos han sido reemplazados por el contenido del archivo. Acaba de descargarse una copia de seguridad de tu estado anterior.',
  'data.delete_confirm': 'Confirmar eliminación',
  'data.delete_warning': 'Todas tus misiones, días, diarios, sesiones y ajustes locales se eliminarán definitivamente. Esta acción es irreversible.',
  'data.delete_success': 'Todos tus datos han sido eliminados. Un nuevo día te espera.',
  'data.import_preview': 'Vista previa de importación',
  'states.loading': 'Cargando el día…',
  'states.storage_unavailable': 'Almacenamiento local no disponible',
  'states.prep_interrupted': 'Preparación local interrumpida',
  'states.prep_failed': 'No se pudo preparar el día',
  'states.diagnostic': 'Diagnóstico técnico',
  'states.empty_eyebrow': 'Espacio disponible',
  'calendar.aria': 'Calendario de actividad',
  'calendar.day_aria': '{date} · {percent}% de progreso',
  'calendar.less': 'Menos',
  'calendar.more': 'Más',
  'calendar.sun': 'D',
  'calendar.mon': 'L',
  'calendar.tue': 'M',
  'calendar.wed': 'X',
  'calendar.thu': 'J',
  'calendar.fri': 'V',
  'calendar.sat': 'S',
  'collapsible.expand': 'Desplegar',
  'collapsible.collapse': 'Plegar',
  'error.loading': 'No se pudo cargar el día.',
  'error.retry': 'Reintentar',
  'error.generic': 'Ocurrió un error.',
  'error.not_found': 'Página no encontrada.',
  'architecture.title': 'Arquitectura',
  'version.label': 'Versión de la aplicación',
  'version.field': 'Versión',
  'version.commit': 'Commit',
  'version.build_date': 'Fecha de compilación',
  // ── Missions (seed MVP) ─────────────────────────────────────────────
  'mission.wake_without_phone.title': 'Despertar sin el teléfono',
  'mission.wake_without_phone.objective': 'Empezar el día sin distracción.',
  'mission.wake_without_phone.instruction.0': 'No consultes las notificaciones.',
  'mission.wake_without_phone.instruction.1': 'Levántate.',
  'mission.wake_without_phone.instruction.2': 'Bebe agua.',
  'mission.wake_without_phone.instruction.3': 'Respira con calma.',
  'mission.mental_preparation.title': 'Preparación mental',
  'mission.mental_preparation.objective': 'Elegir tu estado de ánimo antes de que el día te ocurra.',
  'mission.mental_preparation.instruction.0': 'Respira lentamente durante un minuto.',
  'mission.mental_preparation.instruction.1': 'Identifica tu misión esencial.',
  'mission.mental_preparation.instruction.2': 'Elige la actitud que quieres encarnar.',
  'mission.sport_session.title': 'Sesión de deporte',
  'mission.sport_session.objective': 'Activar el cuerpo y reforzar la disciplina.',
  'mission.sport_session.instruction.0': 'Prepara tu equipamiento.',
  'mission.sport_session.instruction.1': 'Inicia la misión para ver la sesión detallada del día.',
  'mission.sport_session.instruction.2': 'Adapta, reemplaza o ignora cada ejercicio según tu estado.',
  'mission.shower_recovery.title': 'Ducha y recuperación',
  'mission.shower_recovery.objective': 'Volver a la calma después del esfuerzo.',
  'mission.shower_recovery.instruction.0': 'Date una ducha.',
  'mission.shower_recovery.instruction.1': 'Hidrátate.',
  'mission.shower_recovery.instruction.2': 'Vuelve a una respiración tranquila.',
  'mission.day_planning.title': 'Planificación del día',
  'mission.day_planning.objective': 'Dar una dirección clara al día.',
  'mission.day_planning.instruction.0': 'Elige tres prioridades como máximo.',
  'mission.day_planning.instruction.1': 'Identifica la misión esencial.',
  'mission.day_planning.instruction.2': 'Protege un bloque de trabajo profundo.',
  'mission.deep_work.title': 'Trabajo profundo',
  'mission.deep_work.objective': 'Avanzar en la tarea de mayor impacto.',
  'mission.deep_work.instruction.0': 'Desactiva las notificaciones.',
  'mission.deep_work.instruction.1': 'Prepara solo las herramientas necesarias.',
  'mission.deep_work.instruction.2': 'Trabaja sin interrupción durante el bloque previsto.',
  'mission.growth_action.title': 'Acción de crecimiento',
  'mission.growth_action.objective': 'Construir una capacidad o una oportunidad futura.',
  'mission.growth_action.instruction.0': 'Aprende, crea, prospecta o mejora un sistema.',
  'mission.growth_action.instruction.1': 'Elige una acción concreta y medible.',
  'mission.conscious_relationship.title': 'Relación consciente',
  'mission.conscious_relationship.objective': 'Cuidar un vínculo importante sin presión.',
  'mission.conscious_relationship.instruction.0': 'Ofrece atención sincera.',
  'mission.conscious_relationship.instruction.1': 'Escucha o envía un mensaje simple.',
  'mission.conscious_relationship.instruction.2': 'No busques validación inmediata.',
  'mission.evening_review.title': 'Balance de la noche',
  'mission.evening_review.objective': 'Cerrar el día con lucidez.',
  'mission.evening_review.instruction.0': 'Identifica una victoria.',
  'mission.evening_review.instruction.1': 'Anota un aprendizaje.',
  'mission.evening_review.instruction.2': 'Elige la prioridad de mañana.',
  'mission.bedtime_ritual.title': 'Ritual antes de dormir',
  'mission.bedtime_ritual.objective': 'Preparar el descanso y desconectar del día.',
  'mission.bedtime_ritual.instruction.0': 'Aleja el teléfono.',
  'mission.bedtime_ritual.instruction.1': 'Respira lentamente.',
  'mission.bedtime_ritual.instruction.2': 'Suelta el día sin rumiación.',
  'mission.drink_water.title': 'Beber agua',
  'mission.drink_water.objective': 'Mantener el gesto mínimo incluso en un día difícil.',
  'mission.drink_water.instruction.0': 'Bebe un vaso de agua.',
  'mission.conscious_breathing.title': 'Respiración consciente',
  'mission.conscious_breathing.objective': 'Encontrar un punto de anclaje estable.',
  'mission.conscious_breathing.instruction.0': 'Respira lentamente durante unos minutos.',
  'mission.conscious_breathing.instruction.1': 'No busques nada más.',
  'menu.actions_for': 'Acciones para',
  'reorder.position': 'Posición',
  'reorder.position_sur': 'de',
  'reorder.drag': 'Mover',
  'reorder.drag_mission': 'Mover misión',
  'reorder.monter_mission': 'Subir misión',
  'reorder.descendre_mission': 'Bajar misión',
} as const;

// ---------------------------------------------------------------------------
// Catalogues par locale (accès indexé)
// ---------------------------------------------------------------------------

const CATALOGS: Record<Locale, TranslationCatalog> = {
  'fr-FR': fr,
  'en-US': en,
  'es-MX': es,
};

// ---------------------------------------------------------------------------
// Résolution de traduction avec fallback
// ---------------------------------------------------------------------------

/**
 * Résout une clé de traduction dans la locale demandée. Si la clé est absente,
 * retombe sur fr-FR (langue source). Si la clé n'existe pas du tout, retourne
 * la clé elle-même (jamais d'interface cassée).
 */
export function resolveTranslation(key: string, locale: Locale): string {
  const catalog = CATALOGS[locale];
  if (catalog[key]) return catalog[key];
  // Fallback vers fr-FR
  const fallback = CATALOGS['fr-FR'];
  if (fallback[key]) return fallback[key];
  // Clé inconnue : on retourne la clé brute (pas de crash)
  return key;
}

/**
 * Hook de traduction React. Lit la locale active depuis `useAscendLocale`
 * (contexte partagé ou fallback lecture directe) et expose `t(key)`.
 */
export function useTranslation() {
  const locale = useAscendLocale();
  return {
    locale,
    t: (key: string) => resolveTranslation(key, locale),
  };
}

/**
 * Vérifie qu'une locale possède toutes les clés du catalogue source (fr-FR).
 * Retourne la liste des clés manquantes — utile pour les tests et la CI.
 */
export function findMissingKeys(locale: Locale): readonly string[] {
  const source = CATALOGS['fr-FR'];
  const target = CATALOGS[locale];
  return Object.keys(source).filter((key) => !(key in target));
}

/**
 * Vérifie qu'aucune clé du catalogue cible n'est étrangère au catalogue source.
 */
export function findExtraKeys(locale: Locale): readonly string[] {
  const source = CATALOGS['fr-FR'];
  const target = CATALOGS[locale];
  return Object.keys(target).filter((key) => !(key in source));
}
